import type { ReactNode } from 'react'

import { cx } from '../lib/cx'
import { SEVERIDAD_BORDE, type Severidad } from './tokens'

/**
 * Superficie base del panel. Todo lo que es "un bloque" es una Card: el
 * incidente crítico, la tarjeta de KPI, el bloque de deuda de datos.
 *
 * `severidad` pinta el borde izquierdo de 3px. Es refuerzo, no la única señal:
 * el contenido de la card siempre lleva además su pill o su texto de estado.
 */
export interface CardProps {
  children: ReactNode
  /** Borde izquierdo de severidad. Sin esto la card es neutra. */
  severidad?: Severidad
  padding?: 'none' | 'sm' | 'md' | 'lg'
  /** Sombra suave. Reservada para lo que tiene que saltar del fondo. */
  elevada?: boolean
  /** Borde punteado: el vocabulario de "acá falta un dato". */
  punteada?: boolean
  /** Fondo `--bg` en vez de `--surface`: para un bloque hundido. */
  hundida?: boolean
  as?: 'div' | 'article' | 'section' | 'li'
  /** Nombre accesible cuando `as` es `section` o `article`. */
  ariaLabel?: string
  className?: string
}

const PADDING: Record<NonNullable<CardProps['padding']>, string> = {
  none: '',
  sm: 'p-3',
  md: 'p-4',
  lg: 'p-5',
}

export function Card({
  children,
  severidad,
  padding = 'md',
  elevada,
  punteada,
  hundida,
  as = 'div',
  ariaLabel,
  className,
}: CardProps) {
  const Componente = as

  return (
    <Componente
      data-testid="card"
      data-severidad={severidad}
      aria-label={ariaLabel}
      className={cx(
        'min-w-0 rounded-card border',
        punteada === true ? 'border-dashed border-n300' : 'border-border',
        hundida === true ? 'bg-bg' : 'bg-surface',
        severidad !== undefined && ['border-l-[3px]', SEVERIDAD_BORDE[severidad]],
        elevada === true && 'shadow-card',
        PADDING[padding],
        className,
      )}
    >
      {children}
    </Componente>
  )
}
